/**
 * Activates a button without letting the rest of the app observe the interaction.
 *
 * Open action menus, dropdowns and popovers dismiss themselves on a document-level
 * pointer event outside their own subtree. That is exactly what reaching for Bugsnap
 * used to do: the state the user wanted to report was gone before the dialog opened,
 * and gone again when they pressed "Screenshot". Every pointer/touch event on a
 * shielded button is therefore swallowed at the document capture phase — ahead of any
 * document listener and of React's root handler — and activation is driven from here
 * instead of from `onClick`.
 *
 * See ./bugsnapUi for the complementary opt-in that keeps such UI open for the whole
 * lifetime of the dialog.
 */

import { useEffect, useRef } from 'react'

const SHIELDED_EVENTS = [
  'pointerdown',
  'mousedown',
  'pointerup',
  'mouseup',
  'click',
  'touchstart',
  'touchend',
] as const

/**
 * @param onActivate - Run on press (or on keyboard Enter/Space activation). Must be
 *   stable (`useCallback`); the listeners are re-registered when it changes.
 * @returns Ref to attach to the button being shielded.
 */
export function useShieldedTrigger<T extends HTMLElement = HTMLButtonElement>(
  onActivate: () => void,
) {
  const triggerRef = useRef<T | null>(null)

  useEffect(() => {
    const handleTriggerEvent = (event: Event) => {
      const trigger = triggerRef.current
      if (!trigger || !(event.target instanceof Node) || !trigger.contains(event.target)) return

      event.stopPropagation()
      event.stopImmediatePropagation()
      if (event.type === 'mousedown') {
        // Keep focus where it is, so focus/blur-driven popovers survive too. Only
        // `mousedown`: preventing `pointerdown` would suppress the compatibility
        // mouse events this handler activates on.
        event.preventDefault()
      }

      // Activate on press for pointers, and on the synthetic click that keyboard
      // activation (Enter/Space) produces — that one carries `detail === 0`.
      const keyboardClick = event.type === 'click' && (event as MouseEvent).detail === 0
      if (event.type === 'mousedown' || keyboardClick) {
        onActivate()
      }
    }

    SHIELDED_EVENTS.forEach((type) =>
      document.addEventListener(type, handleTriggerEvent, true),
    )
    return () =>
      SHIELDED_EVENTS.forEach((type) =>
        document.removeEventListener(type, handleTriggerEvent, true),
      )
  }, [onActivate])

  return triggerRef
}
